import React, { useCallback, useMemo, useState } from "react";

import { type DocumentNode } from "graphql";
import {
  cloneDeep,
  compact,
  flatten,
  isEmpty,
  isEqual,
  orderBy,
  uniq,
} from "lodash";

import { gql } from "@apollo/client";
import { useUser } from "@components/user-context";
import { useUniversalBenchmarksMetrics } from "@features/universal-benchmarks";
import { useFeatureFlags, useNorthbeamLazyQuery } from "@hooks/http";
import { usePrevious } from "@hooks/use-previous";
import {
  type GetSalesReportV3Augmented,
  type GetSalesReportV3AugmentedVariables,
  type SalesPageMetric,
} from "@north-beam/nb-client-side";
import {
  AccountingModes,
  type DateInterval,
  type IGranularity,
  type IMCAttributionWindowDays,
  type INBAccountingMode,
  type ITimeGranularity,
} from "@north-beam/nb-common";

import { type Dimensions } from "../charts/chart";
import { FIXED_NAME_COLUMN_ID } from "../table/components/constants";
import { isKeyParent, isParent } from "../table/components/table";

import { useAvailableMetrics } from "./use-available-metrics";
import { usePageConfiguration } from "./use-page-configuration";
import { type AdKeyInfo, useActiveTabState } from "./use-tab-state";
import {
  useDefaultTableSelectionIsSelectAll,
  useSplitView,
} from "./use-table-selection";

export interface SalesReportProps {
  dateRange: DateInterval;
  compareDateRange: DateInterval;
  attributionModel: string;
  attributionWindow: IMCAttributionWindowDays;
  accountingMode: INBAccountingMode;
  timeGranularity: ITimeGranularity;
}

export enum GRANULARITY {
  CAMPAIGN = "campaign",
  ADSET = "adset",
  AD = "ad",
}

export const SALES_TABLE_LIMIT = 30;
export function useLazyLoadSalesReport<
  Query,
  Variables extends GetSalesReportV3AugmentedVariables,
>(query: DocumentNode) {
  const {
    loadData,
    data: { data, loading, error, refetch },
  } = useNorthbeamLazyQuery<Query, Variables>(query, {
    returnPartialData: true,
    doNotRaiseErrorOnFailure: true,
  });

  return { loadData, data, loading, error, refetch };
}

export function useSalesReportV3<
  Variables extends GetSalesReportV3AugmentedVariables,
>(
  granularity: IGranularity,
  variables: Omit<GetSalesReportV3AugmentedVariables, "granularity">,
) {
  const commonGQLVariables = variables;

  const campaignVariables = useMemo(() => {
    return {
      granularity: GRANULARITY.CAMPAIGN,
      ...commonGQLVariables,
    } as Variables;
  }, [commonGQLVariables]);
  const {
    loadData: loadCampaignData,
    data: campaignData,
    loading: campaignLoading,
    error: campaignError,
    refetch: campaignRefetch,
  } = useLazyLoadSalesReport<GetSalesReportV3Augmented, Variables>(
    SALES_REPORT_V3_AUGMENTED,
  );

  const adsetVariables = useMemo(() => {
    return {
      granularity: GRANULARITY.ADSET,
      ...commonGQLVariables,
    } as Variables;
  }, [commonGQLVariables]);
  const {
    loadData: loadAdsetData,
    data: adsetData,
    loading: adsetLoading,
    error: adsetError,
    refetch: adsetRefetch,
  } = useLazyLoadSalesReport<GetSalesReportV3Augmented, Variables>(
    SALES_REPORT_V3_AUGMENTED,
  );

  const adVariables = useMemo(() => {
    return {
      granularity: GRANULARITY.AD,
      ...commonGQLVariables,
    } as Variables;
  }, [commonGQLVariables]);
  const {
    loadData: loadAdData,
    data: adData,
    loading: adLoading,
    error: adError,
    refetch: adRefetch,
  } = useLazyLoadSalesReport<GetSalesReportV3Augmented, Variables>(
    SALES_REPORT_V3_AUGMENTED,
  );

  const loadAllData = useCallback(
    (additionalVariables?: Partial<Variables>) => {
      if (granularity === "campaign" || granularity === "platform") {
        loadCampaignData({
          variables: {
            ...campaignVariables,
            ...(additionalVariables && additionalVariables),
          },
        });
      } else if (granularity === "adset") {
        loadAdsetData({
          variables: {
            ...adsetVariables,
            ...(additionalVariables && additionalVariables),
          },
        });
      } else {
        loadAdData({
          variables: {
            ...adVariables,
            ...(additionalVariables && additionalVariables),
          },
        });
      }
    },
    [
      granularity,
      loadCampaignData,
      campaignVariables,
      loadAdsetData,
      adsetVariables,
      loadAdData,
      adVariables,
    ],
  );

  const { data, isLoading, error, refetch } = useMemo(() => {
    let data = campaignData;
    let isLoading = campaignLoading;
    let error = campaignError;
    let refetch = campaignRefetch;
    if (granularity === GRANULARITY.ADSET) {
      data = adsetData;
      isLoading = adsetLoading;
      error = adsetError;
      refetch = adsetRefetch;
    } else if (granularity === GRANULARITY.AD) {
      data = adData;
      isLoading = adLoading;
      error = adError;
      refetch = adRefetch;
    }

    return {
      data,
      isLoading,
      error,
      refetch,
    };
  }, [
    adData,
    adError,
    adLoading,
    adRefetch,
    adsetData,
    adsetError,
    adsetLoading,
    adsetRefetch,
    campaignData,
    campaignError,
    campaignLoading,
    campaignRefetch,
    granularity,
  ]);

  const loadOnlyAdsetsData = useCallback(
    (additionalVariables?: Partial<Variables>) => {
      loadCampaignData({ variables: { ...campaignVariables, fakeCall: true } });
      loadAdData({ variables: { ...adVariables, fakeCall: true } });
      return loadAdsetData({
        variables: {
          ...adsetVariables,
          ...(additionalVariables && additionalVariables),
        },
      });
    },
    [
      adVariables,
      adsetVariables,
      campaignVariables,
      loadAdData,
      loadAdsetData,
      loadCampaignData,
    ],
  );

  const loadOnlyCampaignData = useCallback(
    (additionalVariables?: Partial<Variables>) => {
      loadAdData({ variables: { ...adVariables, fakeCall: true } });
      loadAdsetData({ variables: { ...adsetVariables, fakeCall: true } });
      return loadCampaignData({
        variables: {
          ...campaignVariables,
          ...(additionalVariables && additionalVariables),
        },
      });
    },
    [
      adVariables,
      adsetVariables,
      campaignVariables,
      loadAdData,
      loadAdsetData,
      loadCampaignData,
    ],
  );

  const loadOnlyAdData = useCallback(
    (additionalVariables?: Partial<Variables>) => {
      loadCampaignData({ variables: { ...campaignVariables, fakeCall: true } });
      loadAdsetData({ variables: { ...adsetVariables, fakeCall: true } });
      return loadAdData({
        variables: {
          ...adVariables,
          ...(additionalVariables && additionalVariables),
        },
      });
    },
    [
      adVariables,
      adsetVariables,
      campaignVariables,
      loadAdData,
      loadAdsetData,
      loadCampaignData,
    ],
  );

  return {
    loadOnlyAdsetsData,
    loadOnlyCampaignData,
    loadOnlyAdData,
    loadAllData,
    data,
    isLoading,
    error,
    refetch,
  };
}

const resolve = (
  metric: SalesPageMetric,
  getMetricById: (id: string) => SalesPageMetric,
): SalesPageMetric[] => {
  if (metric?.requiredNBCalculatedMetrics) {
    return metric.requiredNBCalculatedMetrics.flatMap((e) =>
      resolve(getMetricById(e), getMetricById),
    );
  } else {
    return [metric];
  }
};

export const resolveBaseMetrics = (
  m: SalesPageMetric,
  getMetricById: (id: string) => SalesPageMetric,
) => {
  const baseMetrics = m.baseMetrics?.length
    ? m.baseMetrics.flatMap((e) => e.requiredBaseMetrics)
    : m.requiredBaseMetrics;

  const calculatedMetrics = m?.requiredNBCalculatedMetrics
    ? resolve(m, getMetricById)
    : [];

  return uniq([
    ...baseMetrics,
    ...calculatedMetrics.flatMap((i) => i.requiredBaseMetrics),
  ]);
};

export const useLoadSalesData = (metrics: SalesPageMetric[]) => {
  const { user } = useUser();
  const prevMetrics = usePrevious(metrics);
  const hasForecastingChanged = useMemo(() => {
    const currentHasForecasting =
      metrics.some((m) => m.id.toLowerCase().includes("forecast")) ?? false;
    const prevHasForecasting =
      prevMetrics?.some((m) => m.id.toLowerCase().includes("forecast")) ??
      false;
    return currentHasForecasting === prevHasForecasting;
  }, [metrics, prevMetrics]);

  const { hasFeatureFlag } = useFeatureFlags();

  const abortController = React.useRef<AbortController>();
  const abortLatest = useCallback(() => {
    abortController?.current &&
      abortController.current.abort("ABORT_SALES_DATA_FETCHING");
  }, []);

  const { loadData: loadSalesReportV3, data } = useNorthbeamLazyQuery<
    GetSalesReportV3Augmented,
    GetSalesReportV3AugmentedVariables
  >(SALES_REPORT_V3_AUGMENTED);

  const { getMetricById } = useAvailableMetrics();

  const {
    accountingMode,
    attributionModel,
    attributionWindow,
    dateRangeInterval,
    breakdowns,
    timeGranularity,
    granularity,
    filters,
    sorting,
    sortingOrder,
    metricFilters,
    searchQuery,
    statuses,
  } = usePageConfiguration();

  const { metricsV1: benchmarkMetrics } = useUniversalBenchmarksMetrics({
    attributionModel,
    attributionWindow,
    accountingMode,
  });

  const cleanMetricFilters = useMemo(() => {
    return isEmpty(metricFilters) ? null : metricFilters;
  }, [metricFilters]);

  const allMetrics = useMemo(() => {
    const metricsFiltersMetrics = flatten(
      cleanMetricFilters?.metricClauses?.map((m) =>
        resolveBaseMetrics(getMetricById(m.metric.id), getMetricById),
      ),
    );

    const sortingSalesPageSpec =
      sorting?.metric.metric && sorting?.metric?.id !== FIXED_NAME_COLUMN_ID
        ? getMetricById(sorting.metric.id)
        : null;
    const sortingMetrics = sortingSalesPageSpec
      ? resolveBaseMetrics(sortingSalesPageSpec, getMetricById)
      : [];

    const flattenedMetrics = flatten(
      metrics.map((m) => resolveBaseMetrics(m, getMetricById)),
    );

    const flattenedBenchmarkMetrics = benchmarkMetrics.flatMap(
      (e) => e.requiredBaseMetrics,
    );

    if (flattenedBenchmarkMetrics.length > 0) {
      flattenedBenchmarkMetrics.push("spend");
    }

    //additional shops metrics
    const all = uniq([
      ...flattenedMetrics,
      ...sortingMetrics,
      ...metricsFiltersMetrics,
      ...flattenedBenchmarkMetrics,
    ]);
    if (all.includes("rev")) {
      all.push("revenueWebShops", "revenueMetaShops", "revenueTikTokShops");
    }

    if (all.includes("txns")) {
      all.push(
        "transactionsWebShops",
        "transactionsMetaShops",
        "transactionsTikTokShops",
      );
    }

    if (sorting?.type === "user_defined") {
      all.push("customGoals");
    }

    if (metrics.find(({ id }) => id === "touchpoints")) {
      all.push("rev");
    }

    return orderBy(uniq(all));
  }, [
    cleanMetricFilters?.metricClauses,
    getMetricById,
    metrics,
    sorting,
    benchmarkMetrics,
  ]);

  const { isBreakdownTab, tabState } = useActiveTabState();
  const prevAppliedState = usePrevious(tabState.appliedFiltersState);

  const hasAppliedStateChanged = useMemo(() => {
    return !isEqual(prevAppliedState, tabState.appliedFiltersState);
  }, [prevAppliedState, tabState.appliedFiltersState]);

  const isSplitView = useSplitView();
  const { isDefaultSelectAll } = useDefaultTableSelectionIsSelectAll();

  const hasForecastMetric = metrics.some((m) =>
    m.id.toLowerCase().includes("forecast"),
  );
  const showV1Forecasts =
    timeGranularity !== "hourly" &&
    attributionModel === "northbeam_custom" &&
    accountingMode === AccountingModes.ACCRUAL &&
    hasFeatureFlag("showV1Forecasts") &&
    hasForecastMetric;

  const mainVariables = useMemo(() => {
    const breakdownFilters: {
      breakdown: string;
      value: string;
    }[] = flatten(
      filters.map((breakdown) =>
        breakdown.values.map((value) => ({
          breakdown: breakdown.key,
          value,
        })),
      ),
    );

    const exclusionBreakdownFilters =
      tabState.appliedFiltersState?.exclusionAtomicities.map(
        ({ atomicity }) => {
          const parsed = JSON.parse(atomicity) as Dimensions[];
          return flatten(
            parsed.map((dimensions) => {
              return Object.keys(dimensions).map((key) => ({
                breakdown: key,
                value: dimensions[key],
              }));
            }),
          );
        },
      );

    const inclusionBreakdownFilters =
      tabState.appliedFiltersState?.inclusionAtomicities.map(
        ({ atomicity }) => {
          const parsed = JSON.parse(atomicity) as Dimensions[];
          return flatten(
            parsed.map((dimensions) => {
              return Object.keys(dimensions).map((key) => ({
                breakdown: key,
                value: dimensions[key],
              }));
            }),
          );
        },
      );

    let syncedMetricFilters = null;

    if (cleanMetricFilters) {
      syncedMetricFilters = cloneDeep(cleanMetricFilters);

      syncedMetricFilters.metricClauses.forEach((metricClause) => {
        if (metricClause.metric.details) {
          if (metricClause.metric.details.am) {
            metricClause.metric.details.am = attributionModel;
          }

          if (metricClause.metric.details.aw) {
            metricClause.metric.details.aw = attributionWindow;
          }

          if (metricClause.metric.details.accountingMode) {
            metricClause.metric.details.accountingMode = accountingMode;
          }
        }
      });
    }

    const queryVariables = {
      metrics: allMetrics,
      queryType: isSplitView
        ? "specific"
        : !isDefaultSelectAll
        ? "exclusive"
        : "inclusive",
      showV1Forecasts,
      dateRange: dateRangeInterval,
      compareDateRange: null,
      attributionMethod: attributionModel,
      attributionWindow,
      accountingMode,
      timeGranularity: timeGranularity,
      offset: 0,
      limit: SALES_TABLE_LIMIT,
      statuses,
      compareHourlyCutsShort: true,
      inclusionBreakdownFilters,
      exclusionBreakdownFilters,
      exclusionCampaignFilters:
        tabState.appliedFiltersState?.campaignExclusionAdKeys?.map(
          (i) => i.adKey,
        ),
      exclusionAdsetFilters:
        tabState.appliedFiltersState?.adsetExclusionAdKeys?.map((i) => i.adKey),
      campaignFilters: tabState.appliedFiltersState?.campaignInclusionAdKeys,
      adsetFilters: tabState.appliedFiltersState?.adsetInclusionAdKeys,
      search:
        searchQuery && searchQuery.query !== ""
          ? {
              method: searchQuery.method,
              operator: searchQuery.operator,
              query: searchQuery.query,
            }
          : null,
      creativeType: null,
      atomicity: isBreakdownTab ? breakdowns?.map((i) => i.key) : null,
      deepestAtomicity: isBreakdownTab ? breakdowns?.map((i) => i.key) : null,
      breakdownFilters,
      dateCompression: true,
      aggregateAll: false,
      granularity,
      metricFilters: syncedMetricFilters,
      sorting: !sorting
        ? {
            customComposed: false,
            metric: {
              id: "spend",
              metric: "spend",
              details: {
                accountingMode,
                am: attributionModel,
                aw: attributionWindow,
                type: "",
              },
            },
            type: "base",
            order: sortingOrder,
          }
        : {
            customComposed: sorting.customComposed,
            type: sorting.type,
            metric: {
              ...sorting.metric,
              details: {
                accountingMode,
                am: attributionModel,
                aw: attributionWindow,
                type: "",
                ...sorting.metric?.details,
              },
            },
            order: sortingOrder,
          },
    } as GetSalesReportV3AugmentedVariables;
    return queryVariables;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    accountingMode,
    attributionModel,
    attributionWindow,
    breakdowns,
    cleanMetricFilters,
    dateRangeInterval,
    filters,
    granularity,
    hasAppliedStateChanged,
    hasForecastingChanged,
    isBreakdownTab,
    isDefaultSelectAll,
    isSplitView,
    searchQuery,
    showV1Forecasts,
    sorting,
    sortingOrder,
    statuses,
    timeGranularity,
    allMetrics,
  ]);

  const loadSalesTable = useCallback(
    async (variables: Partial<GetSalesReportV3AugmentedVariables>) => {
      const controller = new window.AbortController();
      abortController.current = controller;

      /* this endpoint inclusion variables is only used for the nesting
         filter out the parents */
      let childrenOfVariableInclusion: string[] | undefined;

      let exclusionCampaignFilters: string[] | undefined;
      let exclusionAdsetFilters: string[] | undefined;
      let campaignFilters: AdKeyInfo[] | undefined;
      let adsetFilters: AdKeyInfo[] | undefined;
      if (variables.inclusionBreakdownFilters) {
        const variableInclusionAtom = variables.inclusionBreakdownFilters?.map(
          (breakdownFilter) => {
            return JSON.stringify(
              breakdownFilter.map((b) => ({
                [b?.breakdown as string]: b?.value,
              })),
            );
          },
        )[0] as string;
        const mainInclusionAtoms = mainVariables.inclusionBreakdownFilters?.map(
          (breakdownFilter) => {
            return JSON.stringify(
              breakdownFilter.map((b) => ({
                [b?.breakdown as string]: b?.value,
              })),
            );
          },
        );

        childrenOfVariableInclusion = mainInclusionAtoms?.filter((atom) =>
          isParent(variableInclusionAtom, atom),
        );

        exclusionCampaignFilters = compact(
          tabState.appliedFiltersState?.campaignExclusionAdKeys?.map((i) =>
            isKeyParent(i.dimensions, variableInclusionAtom) ? i.adKey : null,
          ),
        );
        exclusionAdsetFilters = compact(
          tabState.appliedFiltersState?.adsetExclusionAdKeys?.map((i) =>
            isKeyParent(i.dimensions, variableInclusionAtom) ? i.adKey : null,
          ),
        );
        campaignFilters = compact(
          tabState.appliedFiltersState?.campaignInclusionAdKeys?.map((i) =>
            isKeyParent(i.dimensions, variableInclusionAtom) ? i : null,
          ),
        );
        adsetFilters = compact(
          tabState.appliedFiltersState?.adsetInclusionAdKeys?.map((i) =>
            isKeyParent(i.dimensions, variableInclusionAtom) ? i : null,
          ),
        );
      }

      const queryVariables = {
        ...mainVariables,
        ...variables,
        includeLabelsSearch: isBreakdownTab,
        metrics: allMetrics,
        exclusionCampaignFilters: variables?.inclusionBreakdownFilters
          ? exclusionCampaignFilters
          : mainVariables.exclusionCampaignFilters,
        exclusionAdsetFilters: variables?.inclusionBreakdownFilters
          ? exclusionAdsetFilters
          : mainVariables.exclusionAdsetFilters,
        campaignFilters: variables?.inclusionBreakdownFilters
          ? campaignFilters
          : mainVariables.campaignFilters,
        adsetFilters: variables?.inclusionBreakdownFilters
          ? adsetFilters
          : mainVariables.adsetFilters,
        inclusionBreakdownFilters: variables?.inclusionBreakdownFilters
          ? [childrenOfVariableInclusion, campaignFilters, adsetFilters].some(
              (i) => i?.length,
            )
            ? childrenOfVariableInclusion?.map((stringifiedAtom) => {
                const parsed = JSON.parse(stringifiedAtom) as Dimensions[];

                return flatten(
                  parsed.map((dimensions) => {
                    return Object.keys(dimensions).map((key) => ({
                      breakdown: key,
                      value: dimensions[key],
                    }));
                  }),
                );
              })
            : variables.inclusionBreakdownFilters
          : mainVariables.inclusionBreakdownFilters,
      } as GetSalesReportV3AugmentedVariables;

      const result = await loadSalesReportV3({
        variables: queryVariables,
        context: {
          fetchOptions: {
            signal: abortController.current?.signal,
          },
        },
      });

      const resultData = result?.data?.me;

      return {
        isRequestAborted: controller.signal.aborted,
        queryMetadata: resultData?.salesTableReportV3Augmented?.queryMetadata,
        actual: {
          rows: resultData?.salesTableReportV3Augmented?.actual?.rows,
          customGoals:
            resultData?.salesTableReportV3Augmented?.actual?.customGoals,
          hasMore: resultData?.salesTableReportV3Augmented?.actual?.hasMore,
          hasV1Forecasts:
            resultData?.salesTableReportV3Augmented?.actual?.hasV1Forecasts,
        },
        comparison: resultData?.salesTableReportV3Augmented?.comparison
          ? {
              rows: resultData.salesTableReportV3Augmented?.comparison?.rows,
              customGoals:
                resultData.salesTableReportV3Augmented.comparison.customGoals,
              hasMore:
                resultData.salesTableReportV3Augmented.comparison.hasMore,
              hasV1Forecasts:
                resultData.salesTableReportV3Augmented.comparison
                  .hasV1Forecasts,
            }
          : null,
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mainVariables, user.apolloClient],
  );

  return { loadSalesTable, loading: data.loading, abort: abortLatest } as const;
};

export const SALES_REPORT_V3_AUGMENTED = gql`
  query GetSalesReportV3Augmented(
    $dateRange: JSONObject!
    $compareDateRange: JSONObject
    $attributionMethod: String!
    $accountingMode: String!
    $timeGranularity: String!
    $attributionWindow: String!
    $granularity: String!
    $sortedAdKeys: [String!]
    $adKeys: [String!]
    $dateCompression: Boolean
    $offset: Int
    $limit: Int
    $search: SalesTableReportSearchInput
    $statuses: [CreativesStatusEnum!]
    $creativeType: CreativesTypeEnum
    $atomicity: [String!]
    $deepestAtomicity: [String!]
    $metricFilters: SalesTableReportV3MetricsFilterInput
    $sorting: SalesTableReportSortingInput
    $campaignFilters: [SalesTableReportV3AdkeyFilter!]
    $adsetFilters: [SalesTableReportV3AdkeyFilter!]
    $adFilters: [SalesTableReportV3AdkeyFilter!]
    $breakdownFilters: [SalesTableReportV3BreakdownsFilterInput]
    $queryType: SalesTableTypeEnum!
    $overridingInclusionBreakdownFilters: [[SalesTableReportV3BreakdownsFilterInput]!]
    $inclusionBreakdownFilters: [[SalesTableReportV3BreakdownsFilterInput]!]
    $exclusionBreakdownFilters: [[SalesTableReportV3BreakdownsFilterInput]!]
    $aggregateAll: Boolean
    $showV1Forecasts: Boolean
    $preload: Boolean
    $exclusionCampaignFilters: [String!]
    $exclusionAdsetFilters: [String!]
    $exclusionAdFilters: [String!]
    $compareHourlyCutsShort: Boolean
    $metrics: [String!]
    $includeLabelsSearch: Boolean
  ) {
    me {
      salesTableReportV3Augmented(
        queryType: $queryType
        dateRange: $dateRange
        compareDateRange: $compareDateRange
        attributionMethod: $attributionMethod
        accountingMode: $accountingMode
        timeGranularity: $timeGranularity
        attributionWindow: $attributionWindow
        granularity: $granularity
        sortedAdKeys: $sortedAdKeys
        adKeys: $adKeys
        dateCompression: $dateCompression
        offset: $offset
        limit: $limit
        search: $search
        statuses: $statuses
        creativeType: $creativeType
        atomicity: $atomicity
        deepestAtomicity: $deepestAtomicity
        metricFilters: $metricFilters
        sorting: $sorting
        campaignFilters: $campaignFilters
        adsetFilters: $adsetFilters
        adFilters: $adFilters
        breakdownFilters: $breakdownFilters
        inclusionBreakdownFilters: $inclusionBreakdownFilters
        overridingInclusionBreakdownFilters: $overridingInclusionBreakdownFilters
        exclusionBreakdownFilters: $exclusionBreakdownFilters
        aggregateAll: $aggregateAll
        showV1Forecasts: $showV1Forecasts
        preload: $preload
        exclusionCampaignFilters: $exclusionCampaignFilters
        exclusionAdsetFilters: $exclusionAdsetFilters
        exclusionAdFilters: $exclusionAdFilters
        compareHourlyCutsShort: $compareHourlyCutsShort
        metrics: $metrics
        includeLabelsSearch: $includeLabelsSearch
      ) {
        comparison {
          rows {
            dimensions
            deepestAtomicityCounts
            dailyMetrics
            name
            statuses
            adName
            adsetName
            campaignName
            imageUri
            objectId
            link
            nbtPlatformID
            cellAnnotations
            forecastV1Daily
            key
          }
          hasMore
          hasV1Forecasts
          customGoals {
            id
            displayName
          }
        }

        queryMetadata {
          bqLogs {
            query
            queryParameters
            queryTypes
            labels
            jobId
            queryId
            executionTimestamp
          }
        }

        actual {
          rows {
            dimensions
            deepestAtomicityCounts
            dailyMetrics
            name
            statuses
            adName
            adsetName
            campaignName
            imageUri
            objectId
            link
            nbtPlatformID
            cellAnnotations
            forecastV1Daily
            key
          }
          hasMore
          hasV1Forecasts
          customGoals {
            id
            displayName
          }
        }
      }
    }
  }
`;
